import { motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { fetchJob } from '~/actions';
import { Button, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Center, Column, ConfirmPopup, Row } from '~/commonComponents';
import { TextAreaField } from '~/components/shared/forms';
import {
  CancellationReasonJobType,
  ICancellationReason,
  useGetCancellationReasons,
  useSubmitCancellationFeedback,
} from '~/domain/JobCancellation';
import { showErrorMessage } from '~/domain/utils';
import browserHistory from '~/browserHistory';
import { useAppSelector } from '~/hooks';
import checkedCircleIcon from '~/images/checkedCircleIcon.svg';

import { POSTHOG_EVENTS, trackPosthogEvent } from '~/services/posthog';
import AnimatedCheckCircle from '../shared/AnimatedCheckCircle';
import { IPermCancellationEventProps, IShiftCancellationEventProps } from './Types';
import { OutlineCircleIcon } from '@tempmee/react-icons';

const SuccessIconComponent = () => <AnimatedCheckCircle />;

export const CancellationFeedback = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const { jobId } = params;

  const [selectedReason, setSelectedReason] = useState<ICancellationReason | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');

  const currentJob = useAppSelector((state) => state.job?.job);
  const { data: reasons } = useGetCancellationReasons({
    jobType: currentJob?.isOnsiteInterview
      ? CancellationReasonJobType.Interview
      : CancellationReasonJobType.Shift,
  });

  const { mutate: submitFeedback, isPending: isSubmitting } = useSubmitCancellationFeedback();

  const isFormInvalid =
    selectedReason === null || (selectedReason.name === 'Other' && cancellationReason?.length < 3);

  useEffect(() => {
    if (jobId) {
      dispatch(fetchJob({ jobId }));
    }
  }, [dispatch, jobId]);

  const onSubmitFeedback = useCallback(() => {
    if (!jobId || !selectedReason) return;

    submitFeedback(
      {
        jobId,
        cancellationFeedback: selectedReason.code,
        cancellationReason,
      },
      {
        onSuccess: () => {
          if (currentJob?.isOnsiteInterview) {
            trackPosthogEvent(POSTHOG_EVENTS.OFFICE_PERM_INTERVIEW_CANCELLATION_REASON_SUBMITTED, {
              officeId: currentJob?.offer_owner?.id,
              onsiteInterviewId: currentJob?.id,
              postingId: currentJob?.postingId,
              reason: selectedReason.name,
              othersReason: cancellationReason,
            } satisfies IPermCancellationEventProps);
            browserHistory.replace(`/permanent-jobs/job-overview/${currentJob?.postingId}`);
          } else {
            trackPosthogEvent(POSTHOG_EVENTS.OFFICE_SHIFT_CANCELLATION_REASON_SUBMITTED, {
              officeId: currentJob?.offer_owner?.id,
              shiftId: currentJob?.id,
              reason: selectedReason.name,
              othersReason: cancellationReason,
            } satisfies IShiftCancellationEventProps);
            browserHistory.push('/dashboard/home');
          }
        },
        onError: (error) => {
          showErrorMessage({ error });
        },
      },
    );
  }, [
    selectedReason,
    cancellationReason,
    currentJob?.id,
    currentJob?.isOnsiteInterview,
    currentJob?.offer_owner?.id,
    currentJob?.postingId,
    jobId,
    submitFeedback,
  ]);

  return (
    <ConfirmPopup
      modalStyle={{ paddingTop: 30, paddingBottom: 25 }}
      Icon={SuccessIconComponent}
      title="Cancellation Successful!"
      titleStyle={{ marginTop: 20 }}
      description={
        currentJob?.isOnsiteInterview ? (
          <Typography as="span" color={colors.neutral[500]} size="body_large" textAlign="center">
            Help us improve your experience by sharing
            <br />
            why this onsite interview was cancelled.
            <br />
            Please select one option below.
          </Typography>
        ) : (
          <Typography as="span" color={colors.neutral[500]} size="body_large" textAlign="center">
            Help us improve your experience by sharing
            <br />
            why this shift was cancelled. Please select
            <br />
            one option below.
          </Typography>
        )
      }
      descriptionStyle={{ marginTop: 10, marginBottom: 26 }}
    >
      <Column gap={18} width="75%" marginBottom={20}>
        {reasons.map((reason) => (
          <Row
            alignItems="center"
            cursor="pointer"
            data-cy={`cancellation-reason-${reason.name}`}
            gap={10}
            key={reason.code}
            paddingInline={16}
            width="100%"
            onClick={() => {
              setSelectedReason(reason);
              setCancellationReason('');
            }}
          >
            {selectedReason?.code === reason.code ? (
              <img src={checkedCircleIcon} alt="Selected" />
            ) : (
              <OutlineCircleIcon size={24} color={colors.secondary[500]} />
            )}
            <Typography as="span" style={{ cursor: 'pointer' }}>
              {reason.name}
            </Typography>
          </Row>
        ))}
      </Column>

      <motion.div
        initial="hide"
        animate={selectedReason?.name.includes('Other') ? 'show' : 'hide'}
        variants={textAreaVariants}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <TextAreaField
          data-cy="cancellation-details-textarea"
          placeholder="Provide details here..."
          height={60}
          style={{ width: '80%', marginBottom: 20 }}
          value={cancellationReason}
          onChange={(e) => setCancellationReason(e.target.value)}
        />
      </motion.div>
      <Center>
        <Button
          data-cy="submit-cancellation-feedback"
          disabled={isFormInvalid || isSubmitting}
          isLoading={isSubmitting}
          size="md"
          onClick={onSubmitFeedback}
        >
          Submit
        </Button>
      </Center>
    </ConfirmPopup>
  );
};

const textAreaVariants = {
  show: {
    maxHeight: 130,
    opacity: 1,
    display: 'flex',
  },
  hide: {
    maxHeight: 0,
    opacity: 0,
    transitionEnd: { display: 'none' },
  },
};
